import { Directive, ElementRef, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { DeviceService } from '../../modules';
import { FlooriWindow, Orientation } from '../../models';
import { FLOORI_WINDOW } from '../../constants';
import { DragContainerHandleDirective } from '../drag-container-handle';

enum DragEvents {
    start = 'start',
    move = 'move',
    end = 'end',
}

const eventsMap = (isTouch: boolean = true): Record<DragEvents, string> => ({
    [DragEvents.start]: isTouch ? 'touchstart' : 'mousedown',
    [DragEvents.move]: isTouch ? 'touchmove' : 'mousemove',
    [DragEvents.end]: isTouch ? 'touchend' : 'mouseup',
});

@Directive({
    selector: '[flooriDragContainer]',
    providers: [DeviceService],
    standalone: true,
})
export class DragContainerDirective implements OnInit, OnDestroy {
    private readonly device = inject(DeviceService);
    private readonly elRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly windowRef = inject<FlooriWindow>(FLOORI_WINDOW);
    private readonly events = eventsMap(this.device.isMobile);

    private dragHandleEl?: HTMLElement | null;
    private initialPoint?: { x: number; y: number };
    private clickPoint?: { x: number; y: number };
    private delta?: { x: number; y: number };
    private moveMethod?: (ev: MouseEvent | TouchEvent) => void;
    private downMethod?: (ev: MouseEvent | TouchEvent) => void;
    private upMethod?: () => void;

    ngOnInit(): void {
        if (this.device.orientation === Orientation.landscape) {
            this.watchDomChanges();
        }
    }

    ngOnDestroy(): void {
        this.clickPoint = undefined;
        this.delta = undefined;
        this.windowRef.removeEventListener(this.events.move, this.moveMethod as any);
        this.windowRef.removeEventListener(this.events.start, this.downMethod as any);
        this.windowRef.removeEventListener(this.events.end, this.upMethod as any);
    }

    private watchDomChanges(): void {
        const observer = new MutationObserver((mutations: MutationRecord[]) => {
            if (mutations?.length) {
                this.dragHandleEl = (this.elRef.nativeElement as HTMLElement)?.querySelector(
                    `[${DragContainerHandleDirective.handleName}]`,
                );
            }
            if (this.dragHandleEl) {
                this.setupDrag();
                observer.disconnect();
            }
        });

        observer.observe(this.elRef.nativeElement, {
            attributes: true,
            subtree: true,
            attributeFilter: [DragContainerHandleDirective.handleName],
        });
    }

    private setupDrag(): void {
        if (!this.dragHandleEl) {
            return;
        }
        const { x, y } = this.dragHandleEl.getBoundingClientRect();

        this.initialPoint = {
            x: x || 0,
            y: y || 0,
        };
        this.moveMethod = (ev: MouseEvent | TouchEvent): void => this.trackMouseMove(ev);
        this.upMethod = (): void => {
            this.clickPoint = undefined;
            this.delta = undefined;
            this.windowRef.removeEventListener(this.events.move, this.moveMethod as any);
        };
        this.downMethod = (ev: MouseEvent | TouchEvent): void => {
            this.clickPoint = {
                y: this.device.isMobile
                    ? (ev as TouchEvent).touches?.[0].clientY || 0
                    : (ev as MouseEvent).clientY,
                x: this.device.isMobile
                    ? (ev as TouchEvent).touches?.[0].clientX || 0
                    : (ev as MouseEvent).clientX,
            };
            this.delta = undefined;
            this.windowRef?.addEventListener(this.events.move, this.moveMethod as any);
            this.windowRef?.addEventListener(this.events.end, this.upMethod as any);
        };

        this.dragHandleEl?.addEventListener(this.events.start, this.downMethod as any);
    }

    private trackMouseMove(event: MouseEvent | TouchEvent): void {
        const lastX = this.elRef.nativeElement.getBoundingClientRect()?.x;
        const lastY = this.elRef.nativeElement.getBoundingClientRect()?.y;

        const x = this.device.isMobile
            ? (event as TouchEvent).touches?.[0].clientX || 0
            : (event as MouseEvent).clientX;

        if (this.clickPoint && !this.delta) {
            this.delta = {
                x: lastX - (this.clickPoint?.x || 0),
                y: lastY - (this.clickPoint?.y || 0),
            };
        }

        const y = this.device.isMobile
            ? (event as TouchEvent).touches?.[0].clientY || 0
            : (event as MouseEvent).clientY;
        if (!this.device.isMobile) {
            event.preventDefault();
        }
        if (!!this.initialPoint && !!this.delta) {
            this.renderer.setStyle(
                this.elRef.nativeElement,
                'transform',
                `translate(${x - this.initialPoint.x + this.delta.x}px, ${
                    y - this.initialPoint.y + this.delta.y
                }px)`,
            );
        }
    }
}
